import api from '../../api/apiService';
import { Profile } from '../../models/Profile';
import { auth$ } from '../auth';
import { fetchProfile } from '../profile/profile.service';
import DeviceDetector from 'device-detector-js';
import { TrackDeviceProperties, TrackOption, SOURCE_PRODUCT, PreviousPageViewData } from './usageServiceUtils';

//to get device properties related information
const deviceData = new DeviceDetector().parse(window.navigator.userAgent);

//remove null values
const cleanObject = (obj: Partial<TrackDeviceProperties>) => {
  return Object.entries(obj)
    .filter(([, value]) => value !== undefined && value !== null && value !== '')
    .reduce((acc, [key, value]) => {
      acc[key] = value;
      return acc;
    }, {});
};

//implementing the TrackDeviceProperties interface to create request object
const trackDeviceProperties: Partial<TrackDeviceProperties> = cleanObject({
  source: SOURCE_PRODUCT,
  type: deviceData?.device?.type,
  osName: deviceData?.os?.name,
  osVersion: deviceData?.os?.version,
  browserName: deviceData?.client?.name,
  browserVersion: deviceData?.client?.version,
  brand: deviceData?.device?.brand,
  model: deviceData?.device?.model,
});

const TRACK_URI = '/usage/v1/track';
let trackEvents: any[] = [];
let previousPathname: string | null = null;
let previousPathQueryParams: string | null = null;
let previousEvent: string | null = null;
let previousPageViewData: PreviousPageViewData | null = null;

let userProfile: Profile | null = null;
auth$.subscribe(async (token) => {
  if (token) {
    userProfile = await fetchProfile();
    await trackPageView();
  }
});

//tracking time spent per page using time difference between two PAGE_VIEW events
const trackTimeSpentPerPage = async (): Promise<void> => {
  if (previousPageViewData) {
    await trackUsage('TIME_SPENT_PER_PAGE', [
      {
        key: 'pathName',
        value: previousPageViewData.pathName,
      },
      {
        key: 'timeSpent',
        value: `${Date.now() - previousPageViewData.time}`,
      },
    ]);
  }
};

//tracking each PAGE_VIEW based on URL change.
const trackPageView = async (): Promise<void> => {
  if (location.pathname !== previousPathname) {
    const currentPathQueryParams = new URLSearchParams(location.search).toString();
    const trackUsageOptionsPageView = [
      {
        //the path viewed
        key: 'path',
        value: location.pathname,
      },
    ];
    if (currentPathQueryParams) {
      trackUsageOptionsPageView.push({
        //current path's query params
        key: 'currentPathQueryParams',
        value: `${currentPathQueryParams}`,
      });
    }
    if (previousPathname) {
      trackUsageOptionsPageView.push({
        //the path that was viewed before the current page
        key: 'previousPath',
        value: `${previousPathname}`,
      });
    }
    if (previousPathQueryParams) {
      trackUsageOptionsPageView.push({
        //the query params of the previous path that was viewed before the current page
        key: 'previousPathQueryParams',
        value: `${previousPathQueryParams}`,
      });
    }
    await trackUsage('PAGE_VIEW', trackUsageOptionsPageView);
    previousPathname = location.pathname;
    previousPathQueryParams = currentPathQueryParams;
  }
};

const callTrackUsageApi = async () => {
  if (userProfile && trackEvents.length > 0) {
    const array = trackEvents;
    trackEvents = [];
    await api.post(TRACK_URI, array);
  }
};

export const trackUrlChanges = (): void => {
  window.addEventListener('popstate', trackPageView);
  setInterval(callTrackUsageApi, 5000);
};

//function to make request object for track usage API call
export const trackUsage = async (event: string, options?: TrackOption[]): Promise<void> => {
  if (userProfile) {
    const trackUsageData = {
      event,
      user: userProfile.userId,
      staff: userProfile.staff,
      options,
      device: trackDeviceProperties, //to track device information
    };

    //to eliminate multiple entries of same events
    const currentEvent = JSON.stringify(trackUsageData);

    if (previousEvent !== currentEvent) {
      previousEvent = currentEvent;
      trackEvents.push({
        ...trackUsageData,
        time: Date.now(),
      });
    }

    //to track time spent per page
    if (event === 'PAGE_VIEW') {
      trackTimeSpentPerPage();
      previousPageViewData = {
        time: Date.now(),
        pathName: `${previousPathname}`,
      };
    }
  }
};
